import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button } from '@mui/material';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { WithLoadingPropTypes } from 'hocs/withLoading';

import { ConfirmationModalType } from 'constants/confirmationDialog';

import { openConfirmationDialogAction } from 'store/global/actions';
import {
  createServicingInteractionAction,
  deleteServicingInteractionAction,
  updateServicingInteractionAction,
} from 'store/loanApplications/actions';
import { selectServicingInteractions } from 'store/loanApplications/selectors';

import { getServiceLogTableConfig } from '../Servicing.config';
import { buildInteractionPayload } from '../Servicing.utils';

import InteractionDrawer from './InteractionDrawer';
import { headerStyles } from './ServiceLog.styles';
import { ServiceLogTestIds } from './ServiceLog.testIds';

const ServiceLog = ({ isLoading }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const interactions = useSelector(selectServicingInteractions);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingInteraction, setEditingInteraction] = useState(null);

  const sortedInteractions = useMemo(
    () =>
      [...interactions].sort((a, b) => new Date(b.interactionDate) - new Date(a.interactionDate)),
    [interactions],
  );

  const onEdit = useCallback(row => {
    setEditingInteraction(row);
    setIsDrawerOpen(true);
  }, []);

  const onDelete = useCallback(
    row => {
      dispatch(
        openConfirmationDialogAction({
          type: ConfirmationModalType.WARNING,
          message: 'Are you sure you want to delete this interaction?',
          onAcceptButtonName: 'Delete',
          onAccept: () => {
            dispatch(
              deleteServicingInteractionAction({
                applicationId,
                interactionId: row.id,
              }),
            );
          },
        }),
      );
    },
    [dispatch, applicationId],
  );

  const tableConfig = useMemo(
    () =>
      getServiceLogTableConfig({
        onEdit,
        onDelete,
        isLoading,
      }),
    [onEdit, onDelete, isLoading],
  );

  const onLogInteraction = useCallback(() => {
    setEditingInteraction(null);
    setIsDrawerOpen(true);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setEditingInteraction(null);
  }, []);

  const onSave = useCallback(
    async (form, isEditing) => {
      const body = buildInteractionPayload(form);

      if (isEditing) {
        await dispatch(
          updateServicingInteractionAction({
            applicationId,
            interactionId: editingInteraction.id,
            ...body,
          }),
        );
      }

      if (!isEditing) {
        await dispatch(
          createServicingInteractionAction({
            applicationId,
            ...body,
          }),
        );
      }

      setIsDrawerOpen(false);
      setEditingInteraction(null);
    },
    [dispatch, applicationId, editingInteraction],
  );

  return (
    <>
      <Box sx={headerStyles}>
        <Button
          color="primary"
          data-testid={ServiceLogTestIds.LOG_BUTTON}
          disabled={isLoading}
          variant="contained"
          onClick={onLogInteraction}
        >
          Log Interaction
        </Button>
      </Box>

      <TableCDS
        withNoAvailableMessage
        config={tableConfig}
        data={sortedInteractions}
        isLoading={isLoading}
        skeletonRows={3}
      />

      <InteractionDrawer
        interaction={editingInteraction}
        isOpen={isDrawerOpen}
        onClose={onCloseDrawer}
        onSave={onSave}
      />
    </>
  );
};

ServiceLog.propTypes = {
  ...WithLoadingPropTypes,
};

export default memo(ServiceLog);
